import React, { useCallback, useContext, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import {
  Collapse,
  TextTitle,
  ValidatedFixedPanel,
  getPanelErrorProps,
  useCollapseState,
  collapseExpander,
} from '@bingads-webui-campaign/wizard-jumbo-card';
import { ActivityContext } from '@bingads-webui-react/hoc-with-signal';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { AdGroup } from '@bingads-webui/campaign-odata-schemas';
import { ValidationGroup, ErrorMessages } from '@bingads-webui-react/validation';
import { ActuationGroup } from '@bingads-webui-campaign-react/actuator';
import { AttentionBeaconGroup } from '@bingads-webui-campaign-react/attention-beacon';
import { FormErrorGroup, UnmappedFormErrors, entityIs } from '@bingads-webui-campaign-react/form-error';
import { ERROR_ENTITY } from '@bingads-webui-campaign-react/search-campaign-data';
import { withControlErrorHandling, withUnvalidatedControlErrors } from '@bingads-webui-campaign-react/error-handling';
import { useFormService } from '@bingads-webui-react/form';
import { ValidationStringMap } from '@bingads-webui/validation-string-map';
import { buildLegacyI18n } from '@bingads-webui/build-legacy-i18n';
import { useWizardService, WIZARD_EVENTS, getFlowFactor } from '@bingads-webui-react/wizard';
import { SingleLanguageTargetPanel } from '@bingads-webui-campaign-react/wizard-shared-components';
import {
  ValidatedStandardAdGroup, ValidatedDynamicAdGroup,
  ValidatedWebsite as WebsiteControl, ValidatedWebsiteTitle,
  ValidatedDynamicAdTarget as DynamicAdTarget
} from '@bingads-webui-campaign-react/search-campaign-wizard-controls';

import { getStyles } from './styles';
import { StepHeader } from '../../shared/step-header';
import { AD_GROUP_TYPES, AD_GROUP_TYPE_STRINGS } from '../step-utils';
import CampaignNameImg from '../../shared/media/campaign-name.svg';
import AdGroupImg from '../../shared/media/ad-group.svg';
import DynamicAdTargetImg from '../../shared/media/dynamic-ad-target.svg';

const entityIsDynamicSearchAdTarget = entityIs(ERROR_ENTITY.AdGroupDynamicSearchAdTargetError);

const ValidatedWebsite = withControlErrorHandling(withUnvalidatedControlErrors(WebsiteControl));
const ValidatedDynamicAdTarget = withControlErrorHandling(withUnvalidatedControlErrors(DynamicAdTarget, { filter: entityIsDynamicSearchAdTarget }));

export const AdGroupSettingsStep = ({
  data,
  onDataChange,
  error,
}) => {
  const {
    serverErrors = [],
    systemErrors = [],
  } = error || {};

  const { campaign, adGroup } = data || {};
  const {
    [AD_GROUP_TYPES.SearchStandard]: standardAdGroup,
    [AD_GROUP_TYPES.SearchDynamic]: dynamicAdGroup,
  } = adGroup || {};

  const i18n = useContext(I18nContext);
  const legacyI18n = useMemo(() => buildLegacyI18n(i18n, [ValidationStringMap]), [i18n]);
  const classes = useDefaultStyles(getStyles);
  const activity = useContext(ActivityContext);

  const { state: { context: { saveFailed: showAllValidationErrors } } } = useFormService();

  const serverContext = useContext(ServerContext);
  const {
    DynamicPermissions,
    Permissions,
  } = serverContext;
  const permissions = useMemo(() => ({ dynamic: { ...DynamicPermissions }, ...Permissions }), [DynamicPermissions, Permissions]);
  const { IsDSAMixedModeCampaignEnabled } = permissions || {};

  const isExperiment = !!(campaign && campaign.ExperimentId);
  const isMixedMode = IsDSAMixedModeCampaignEnabled && !isExperiment;

  const [
    allAdGroupSettingsSections,
    adGroupSettingsExpandedSections,
    setAdGroupSettingsExpandedSections,
  ] = useCollapseState(
    ['campaignName', 'adGroup', 'dynamicAdTarget', 'language', 'website'] /* all sections */,
    ['campaignName', 'adGroup', 'dynamicAdTarget', 'language'] /* initially expanded sections */
  );

  const basicSettingsExpand = useMemo(
    () => collapseExpander({ setActiveKeys: setAdGroupSettingsExpandedSections }),
    [setAdGroupSettingsExpandedSections]
  );

  const { send, state: wizardState } = useWizardService();
  const currentAdGroupType = getFlowFactor(wizardState, 'adGroupType') || AD_GROUP_TYPES.SearchStandard;
  const onSwitchAdGroupType = useCallback((adGroupType) => {
    send(WIZARD_EVENTS.UPDATE_FlOW_FACTORS, {
      flowFactors: {
        adGroupType,
      },
    });
    activity.signal(`onAdGroupTypeChanged - ${JSON.stringify({
      adGroupType,
    })}`);
  }, [send, activity]);

  if (!isMixedMode && currentAdGroupType === AD_GROUP_TYPES.SearchDynamic) {
    onSwitchAdGroupType(AD_GROUP_TYPES.SearchStandard);
  }

  const onValidateStandardAdGroup = useCallback((result) => {
    if (standardAdGroup) {
      onDataChange({
        data: {
          ...data,
          adGroup: {
            ...data.adGroup,
            [AD_GROUP_TYPES.SearchStandard]: {
              ...standardAdGroup,
              ...result.data,
            },
          },
        },
        isValid: result.isValid,
      });
    }
  }, [data, onDataChange, standardAdGroup]);

  const onValidateDynamicAdGroup = useCallback((result) => {
    if (dynamicAdGroup) {
      onDataChange({
        data: {
          ...data,
          adGroup: {
            ...data.adGroup,
            [AD_GROUP_TYPES.SearchDynamic]: {
              ...dynamicAdGroup,
              ...result.data,
            },
          },
        },
        isValid: result.isValid,
      });
    }
  }, [data, onDataChange, dynamicAdGroup]);

  const standardAdGroupSection = useMemo(() => {
    const campaignNamePanel = (
      <ValidatedFixedPanel
        key="campaignName"
        title={
          <TextTitle
            titleKey={_TL_('Campaign name')}
            topicKey="pop_BA_CampaignCreate_Settings_CampaignName"
            icon={CampaignNameImg}
          />
        }
        attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
      >
        {campaign && campaign.Name}
      </ValidatedFixedPanel>
    );

    const websiteSection = (
      <ValidatedFixedPanel
        key="website"
        title={<ValidatedWebsiteTitle />}
        attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
      >
        <ValidatedWebsite
          validationProps={{
            data: standardAdGroup && standardAdGroup.websites,
            debounce: 700,
          }}
          validateOnRender
          isEmptyValid
          useFluentV2
        />
      </ValidatedFixedPanel>
    );

    const adGroupPanel = (
      <ValidatedFixedPanel
        key="adGroup"
        title={<TextTitle titleKey={_TL_('Ad group')} icon={AdGroupImg} />}
        attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
      >
        <ValidatedStandardAdGroup
          adGroup={standardAdGroup}
          currentAdGroupType={currentAdGroupType}
          onSwitchAdGroupType={onSwitchAdGroupType}
          adGroupTypes={AD_GROUP_TYPES}
          adGroupTypeStrings={AD_GROUP_TYPE_STRINGS}
          isMixedMode={isMixedMode}
        />
      </ValidatedFixedPanel>
    );

    const languagesPanel = (
      <SingleLanguageTargetPanel
        data={standardAdGroup}
        expand={basicSettingsExpand}
        key="language"
      />
    );

    const basicSettings = [
      campaignNamePanel,
      standardAdGroup && websiteSection,
      adGroupPanel,
      languagesPanel,
    ];

    return (
      <div className={classNames('basic-adGroup-section')}>
        <Collapse allPanelItems={allAdGroupSettingsSections} checkItems skipItemsTypeCheck activeKeys={adGroupSettingsExpandedSections} setActiveKeys={setAdGroupSettingsExpandedSections}>
          { basicSettings }
        </Collapse>
      </div>
    );
  }, [campaign, currentAdGroupType, onSwitchAdGroupType, basicSettingsExpand, allAdGroupSettingsSections, adGroupSettingsExpandedSections, setAdGroupSettingsExpandedSections, standardAdGroup]);

  const dynamicAdGroupSection = useMemo(() => {
    const campaignNamePanel = (
      <ValidatedFixedPanel
        key="campaignName"
        title={
          <TextTitle
            titleKey={_TL_('Campaign name')}
            topicKey="pop_BA_CampaignCreate_Settings_CampaignName"
            icon={CampaignNameImg}
          />
        }
        attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
      >
        {campaign && campaign.Name}
      </ValidatedFixedPanel>
    );

    const adGroupPanel = (
      <ValidatedFixedPanel
        key="adGroup"
        title={<TextTitle titleKey={_TL_('Ad group')} icon={AdGroupImg} />}
        attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
      >
        <ValidatedDynamicAdGroup
          adGroup={dynamicAdGroup}
          currentAdGroupType={currentAdGroupType}
          onSwitchAdGroupType={onSwitchAdGroupType}
          adGroupTypes={AD_GROUP_TYPES}
          adGroupTypeStrings={AD_GROUP_TYPE_STRINGS}
        />
      </ValidatedFixedPanel>
    );

    const dynamicAdTargetPanel = (
      <ValidatedFixedPanel
        title={<TextTitle titleKey={_TL_('Dynamic ad targets')} icon={DynamicAdTargetImg} />}
        attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
        key="dynamicAdTarget"
      >
        <ValidatedDynamicAdTarget
          validationProps={{
              data: adGroup && adGroup[AD_GROUP_TYPES.SearchDynamic],
            }}
        />
      </ValidatedFixedPanel>
    );

    const basicSettings = [
      campaignNamePanel,
      adGroupPanel,
      dynamicAdTargetPanel,
    ];

    return (
      <div className={classNames('basic-adGroup-section')}>
        <Collapse allPanelItems={allAdGroupSettingsSections} checkItems skipItemsTypeCheck activeKeys={adGroupSettingsExpandedSections} setActiveKeys={setAdGroupSettingsExpandedSections}>
          { basicSettings }
        </Collapse>
      </div>
    );
  }, [campaign, adGroup, currentAdGroupType, onSwitchAdGroupType, allAdGroupSettingsSections, adGroupSettingsExpandedSections, setAdGroupSettingsExpandedSections, dynamicAdGroup]);

  useEffect(() => {
    activity.signal(`onAdGroupTypeRendered - ${JSON.stringify({
      adGroupType: currentAdGroupType,
    })}`);
  }, [currentAdGroupType, activity]);

  return (
    <div className={classNames(classes.adGroupSettingsStep, 'adgroup-settings-step')}>
      <ActuationGroup actuationProps={{ isActive: showAllValidationErrors }}>
        <AttentionBeaconGroup>
          <FormErrorGroup formErrorProps={{ errors: serverErrors, showAllValidationErrors }}>
            <StepHeader
              title={_TL_('Create an ad group')}
              description={_TL_('Enter your ad group information.')}
            />
            <div className="server-errors">
              <UnmappedFormErrors />
            </div>
            <div className="system-errors">
              <ErrorMessages messages={systemErrors} />
            </div>
            {
              currentAdGroupType === AD_GROUP_TYPES.SearchStandard ? (
                <ValidationGroup
                  validationProps={{
                    schema: AdGroup,
                    onValidate: onValidateStandardAdGroup,
                    data: standardAdGroup || {},
                    i18n: legacyI18n,
                  }}
                >
                  <div className={classes.formContainer}>
                    { standardAdGroupSection }
                  </div>
                </ValidationGroup>
              ) : (
                <ValidationGroup
                  validationProps={{
                    schema: AdGroup,
                    onValidate: onValidateDynamicAdGroup,
                    data: dynamicAdGroup || {},
                    i18n: legacyI18n,
                  }}
                >
                  <div className={classes.formContainer}>
                    { dynamicAdGroupSection }
                  </div>
                </ValidationGroup>
              )
            }
          </FormErrorGroup>
        </AttentionBeaconGroup>
      </ActuationGroup>
    </div>
  );
};

AdGroupSettingsStep.propTypes = {
  data: PropTypes.shape({
    campaign: PropTypes.shape({}).isRequired,
    adGroup: PropTypes.shape({
      SearchStandard: PropTypes.shape({}),
      SearchDynamic: PropTypes.shape({}),
    }).isRequired,
  }).isRequired,
  onDataChange: PropTypes.func.isRequired,
  error: PropTypes.shape({
    serverErrors: PropTypes.arrayOf(PropTypes.shape({})),
    systemErrors: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  partialAdGroup: PropTypes.shape({}).isRequired,
};

AdGroupSettingsStep.defaultProps = {
  error: null,
};
